import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  todayChargebackData,
  getAllUsers,
  overallChargebackData,
  overallChargebackDataOrg,
} from "../../../config/LiveApi";
import "./Recon.css";
import Spinner from "../../Spin/Spinner";
import DownloadReportModal from "../downloadReport/Modal";

const TodaysChargeback = () => {
  const [loading, setLoading] = useState(true);
  const [totals, setTotals] = useState({ count: 0, amount: 0 });
  const [userStats, setUserStats] = useState([]);
  let token = useSelector((store) => store.userReducer.token);
  let role = useSelector((store) => store.userReducer.role);
  const [showOverall, setShowOverall] = useState(false);
  const [showGenerateReport, setShowGenerateReport] = useState(false);

  const formatAmount = (amount) => {
    return `₹${Number(amount).toLocaleString("en-IN")}`;
  };

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);

        // Fetch chargebacks + users in parallel
        const [chargebacks, users] = await Promise.all([
          todayChargebackData(token),
          getAllUsers(token, 1),
        ]);

        // Build userId → alias/id/name map
        const userMap = {};
        (users?.data || []).forEach((u) => {
          userMap[u.id] = u.alias || u.id || u.name;
        });

        // Use ALL chargebacks (no filtering)
        const allChargebacks = chargebacks || [];

        // Calculate totals
        const totalCount = allChargebacks.length;
        const totalAmount = allChargebacks.reduce(
          (sum, item) => sum + Number(item.amount || 0),
          0,
        );

        // Group by user
        const grouped = {};
        allChargebacks.forEach((item) => {
          if (!grouped[item.userId]) {
            grouped[item.userId] = { count: 0, amount: 0 };
          }
          grouped[item.userId].count += 1;
          grouped[item.userId].amount += Number(item.amount || 0);
        });

        // Convert to array + sort by total amount (desc)
        const userStatsArray = Object.entries(grouped)
          .map(([userId, stats]) => ({
            userId,
            name: userMap[userId] || userId, // alias shown here
            count: stats.count,
            amount: stats.amount,
          }))
          .sort((a, b) => b.amount - a.amount);

        setTotals({ count: totalCount, amount: totalAmount });
        setUserStats(userStatsArray);
      } catch (err) {
        console.error("Error fetching chargeback data:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [token]);

  if (loading)
    return (
      <p>
        <Spinner />
      </p>
    );

  return (
    <>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <h3 style={styles.heading}>📊 Today's Chargeback</h3>

          {role === "compliance" && (
            <div style={styles.actionBtns}>
              <button
                style={styles.primaryBtn}
                onClick={() => setShowGenerateReport(true)}
              >
                Generate Report
              </button>

              <button
                style={styles.primaryBtn}
                onClick={() => setShowOverall(true)}
              >
                Chargeback Data
              </button>
              {showOverall && (
                <OverallChargebackData
                  token={token}
                  onClose={() => setShowOverall(false)}
                />
              )}
            </div>
          )}
        </div>
        {/* Summary */}
        <div style={styles.summary}>
          <div style={styles.card}>
            <span>Total Chargebacks</span>
            <strong>{totals.count}</strong>
          </div>

          <div style={styles.card}>
            <span>Total Amount</span>
            <strong>{formatAmount(totals.amount)}</strong>
          </div>
        </div>

        {/* Per User Summary */}
        {/* Table */}
        <h3 style={styles.title}>Chargebacks by User</h3>

        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>User</th>
              <th style={styles.th}>Count</th>
              <th style={styles.th}>Total Amount</th>
            </tr>
          </thead>

          <tbody>
            {userStats.map((user) => (
              <tr key={user.userId}>
                <td style={styles.td}>{user.name}</td>
                <td style={styles.td}>{user.count}</td>
                <td style={styles.td}>{formatAmount(user.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showGenerateReport && (
        <DownloadReportModal onClose={() => setShowGenerateReport(false)} />
      )}
    </>
  );
};

export default TodaysChargeback;

const OverallChargebackData = ({ token, onClose }) => {
  const [tableData, setTableData] = useState([]);
  const [total, setTotal] = useState(0);
  const [view, setView] = useState("merchant"); // merchant | org

  const fetchOverall = async () => {
    const res = await overallChargebackData(token);
    setTableData(res.data);
    setTotal(res.metadata.totalCount);
  };

  const fetchOrgWise = async () => {
    const res = await overallChargebackDataOrg(token);
    setTableData(res.data);
    setTotal(res.metadata.totalCount);
  };

  useEffect(() => {
    fetchOverall();
  }, [token]);

  const handleSwitch = async (type) => {
    setView(type);

    if (type === "merchant") {
      fetchOverall();
    } else {
      fetchOrgWise();
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h3 style={styles.title}>Chargeback Analytics</h3>
          <button style={styles.close} onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Toggle Buttons */}
        <div style={styles.toggleContainer}>
          <button
            style={{
              ...styles.toggleBtn,
              background: view === "merchant" ? "#fc8019" : "#e5e7eb",
              color: view === "merchant" ? "#fff" : "#111",
            }}
            onClick={() => handleSwitch("merchant")}
          >
            Merchant Wise
          </button>

          <button
            style={{
              ...styles.toggleBtn,
              background: view === "org" ? "#fc8019" : "#e5e7eb",
              color: view === "org" ? "#fff" : "#111",
            }}
            onClick={() => handleSwitch("org")}
          >
            Org Wise
          </button>
        </div>

        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead style={styles.thead}>
              <tr>
                <th style={styles.th}>
                  {view === "merchant" ? "Alias" : "Organization"}
                </th>
                <th style={styles.th}>Count</th>
              </tr>
            </thead>

            <tbody>
              {tableData.map((item, index) => (
                <tr
                  key={index}
                  style={{
                    ...styles.tr,
                    background: index % 2 === 0 ? "#fff" : "#fafafa",
                  }}
                >
                  <td style={styles.td}>
                    {view === "merchant" ? item.alias : item.orgName}
                  </td>
                  <td style={styles.td}>{item.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={styles.footer}>
          Total Chargebacks: <span style={styles.total}>{total}</span>
        </div>
      </div>
    </div>
  );
};

const styles = {
  toggleContainer: {
    display: "flex",
    gap: "10px",
    marginBottom: "12px",
  },

  toggleBtn: {
    border: "none",
    padding: "6px 14px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 500,
  },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },

  modal: {
    background: "#fff",
    width: "650px",
    borderRadius: "10px",
    boxShadow: "0 15px 40px rgba(0,0,0,0.2)",
    padding: "20px",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },

  title: {
    marginTop: "20px",
    marginBottom: "10px",
    fontSize: "18px",
    fontWeight: 600,
  },

  close: {
    border: "none",
    background: "none",
    fontSize: "20px",
    cursor: "pointer",
  },

  tableWrapper: {
    maxHeight: "400px",
    overflowY: "auto",
    borderRadius: "6px",
    border: "1px solid #e5e7eb",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    border: "1px solid #e5e7eb",
  },

  thead: {
    background: "#f4f4f4",
    border: "1px solid #ccc",
    position: "sticky",
    top: 0,
  },

  th: {
    padding: "10px",
    textAlign: "center",
    background: "#f3f4f6",
    borderBottom: "1px solid #e5e7eb",
    fontSize: "14px",
    fontWeight: 600,
  },

  td: {
    padding: "10px",
    textAlign: "center",
    borderBottom: "1px solid #eee",
    fontSize: "14px",
  },

  tr: {
    transition: "background 0.2s",
  },

  footer: {
    marginTop: "12px",
    fontWeight: 600,
    textAlign: "right",
  },

  total: {
    color: "#2563eb",
  },
  container: {
    padding: "20px",
    background: "#fff",
  },
  heading: {
    fontSize: "22px",
    fontWeight: 600,
    margin: 0,
    color: "#333",
  },
  actionBtns: {
    display: "flex",
    gap: "10px",
  },
  primaryBtn: {
    background: "#fc8019",
    color: "#fff",
    border: "none",
    padding: "8px 14px",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    boxShadow: "0 3px 8px rgba(0,0,0,0.15)",
  },

  summary: {
    display: "flex",
    gap: "20px",
    marginBottom: "20px",
  },
  card: {
    background: "#f8fafc",
    padding: "12px 18px",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
    display: "flex",
    flexDirection: "column",
    fontSize: "14px",
  },
};
